import { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import api, { ApiError } from '../lib/api';
import useMarkdownImages from './useMarkdownImages';
import type { WikiPageArticle } from '../types/wiki';

// wiki 编辑态的整台状态机：正文草稿、防抖自动存、乐观锁冲突、粘图。
//
// 为什么它在 hook 里而不在 `Wiki/Editor.tsx` 里：改版之后「保存 / 完成 / 已自动
// 保存」这三样搬到了顶条，而顶条是**页面级**的（`WikiPage` 渲染它）。同一台状态
// 机于是有两个渲染者 —— 顶条要 `status` / `saving` / `save`，正文区要 `body` /
// `setBody` / 冲突横幅。
//
// 另外两条路都更差：
// - Editor 里保留状态、再用回调把 save/status 抛给父组件：父组件要为抛上来的
//   每一样各存一份 state，两份状态之间必然有一帧不同步
// - 顶条也交给 Editor 渲染：那就得把面包屑和「历史 / 删除」也搬进 Editor，
//   而它们与「编辑正文」毫无关系
//
// 状态归**同时需要它的最近公共祖先**所有，这里就是页面。hook 是把那台状态机
// 搬过去而不复制的方式。
//
// **没有 beforeunload 拦截**：§6.2 原文那两条补丁在修订里被明确删掉了 ——
// 自动存本身就是防丢失的手段，再加一层拦截是给同一个问题付两次钱。

// 防抖 2 秒（spec §17.5.1）。停手两秒就存一版 auto_saved: true 的，那些版本有
// 100 条的上限、会被裁掉；点「保存」写的是 auto_saved: false 的，永不裁 ——
// 显式保存的作用不是「产生唯一的版本」，是「产生一个不会被裁掉的版本」（§6.2 修订）。
const AUTOSAVE_DELAY = 2000;

type UseWikiEditorArgs = {
  page: WikiPageArticle;
  // 标题的状态在 WikiPage 里：阅读态的 <h1> 与编辑态的输入框必须占同一个位置
  // （都在出处行上面），否则一进编辑态标题就掉到出处行下面，页面的层级当场
  // 倒过来。所以标题当参数传进来，不在这里持有。
  title: string;
  onDone: () => void;
};

export default function useWikiEditor({ page, title, onDone }: UseWikiEditorArgs) {
  const [body, setBody] = useState(page.body_md ?? '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // 撞过 409 之后停掉自动存：每两秒重试一次注定失败的写入，只会把状态行刷成
  // 一条闪烁的噪音（§17.5.1 明确「自动存撞到 409 时不弹窗」）。
  const [conflict, setConflict] = useState<number | null>(null);

  // 进编辑态时那一版的号。每次保存成功换成服务端回来的新号 —— 连着存两次时
  // 第二次不能还拿着第一次之前的号，否则自己把自己撞成冲突。
  const baseVersion = useRef(page.version);
  // 已经落库的内容。自动存拿它比对，没变就不发请求 —— 否则任何一次 re-render
  // 都会白存一版，而那正好是版本洪水本身。
  const savedRef = useRef({ title: page.title, body: page.body_md ?? '' });

  const dirty = title !== savedRef.current.title || body !== savedRef.current.body;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 粘图。先插占位再回填、占位串必须唯一这两件事都在 hook 里（§8.1）——
  // 这里只负责说「传给谁」。
  const {
    takeFiles,
    uploading,
    handlers: imageHandlers,
  } = useMarkdownImages({
    upload: (file: File) => api.wikiImages.create(page.url_key, file),
    setValue: setBody,
    textareaRef,
    onError: setImageError,
  });

  // 一次写入。auto 决定服务端把它记成哪一种版本；label 由服务端按它推导，
  // 前端不传（前端能自己挑 label 就等于给版本列表开一个可以撒谎的入口）。
  const write = async (auto: boolean) => {
    const saved = await api.wikiPages.update(page.url_key, {
      title,
      body_md: body,
      base_version: baseVersion.current,
      auto_saved: auto,
    });
    baseVersion.current = saved.version;
    savedRef.current = { title, body };
    return saved;
  };

  // 防抖自动存。依赖里带 title / body：每敲一个字就重排一次两秒后的计时器，
  // 停手两秒才真的发出去。
  useEffect(() => {
    // uploading > 0 时不自动存：占位串（![上传中…1]()）这时还在正文里，存下去
    // 就是把「上传中」三个字存成了一版内容。照 DetailPane 的备注卡片 —— 那里
    // 同样在 noteUploading > 0 时不提交。
    if (!dirty || conflict !== null || saving || uploading > 0) return undefined;

    const timer = setTimeout(async () => {
      try {
        setStatus('保存中…');
        await write(true);
        setStatus('已自动保存');
        setError(null);
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          // 不弹窗。只在状态行说一句，并停掉自动存 —— 每两秒弹一次确认框
          // 是不可用的（§17.5.1）。
          setConflict((e.data as { current_version?: number })?.current_version ?? null);
          setStatus('有更新，未自动保存');
        } else {
          setStatus(null);
          setError(e instanceof ApiError ? e.message : '没存上');
        }
      }
    }, AUTOSAVE_DELAY);

    return () => clearTimeout(timer);
    // write 每次渲染都是新的闭包，故意不进依赖：放进去会让计时器每次渲染重排，
    // 防抖就永远不会触发。它读的 title / body 已经在依赖里了。
  }, [title, body, dirty, conflict, saving, uploading]);

  // 显式保存。它与自动存的唯一区别是 auto_saved: false —— 那一版不会被裁掉。
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await write(false);
      setStatus(null);
      onDone();
      // 回阅读态并把服务端那一版取回来 —— 出处行要跟着换成「你改的」。
      //
      // 整块 reload（不带 only）：正文改了之后边栏的「最近编辑」顺序也变了，
      // 而 wikiNav 是共享 prop —— 带 only 会把它滤掉，边栏就停在旧顺序上。
      router.reload();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConflict((e.data as { current_version?: number })?.current_version ?? null);
        setStatus(null);
      } else {
        setError(e instanceof ApiError ? e.message : '没存上');
      }
    } finally {
      setSaving(false);
    }
  };

  // 「仍然覆盖」：拿 409 带回来的 current_version 当新的 base_version 再发一次。
  // 被覆盖的那一版还在 wiki_page_versions 里 —— 这正是 §6 那套版本历史换来的
  // 安全网，也是「丢失更新退化成多了一版要合并」那句话的兑现处（§6.6）。
  const overwrite = () => {
    if (conflict !== null) baseVersion.current = conflict;
    setConflict(null);
    save();
  };

  return {
    body,
    setBody,
    textareaRef,
    imageHandlers,
    takeFiles,
    uploading,
    imageError,
    status,
    error,
    saving,
    dirty,
    conflict,
    save,
    overwrite,
  };
}

export type WikiEditorState = ReturnType<typeof useWikiEditor>;
